/**
 * Firebase Authentication Provider Configurations
 *
 * This module exports pre-configured OAuth providers for Firebase Authentication.
 * Each provider is configured with appropriate scopes and custom parameters.
 *
 * Providers:
 * - Google OAuth 2.0 (existing, maintained for compatibility)
 * - Apple Sign-In (Phase 1)
 * - Microsoft OAuth 2.0 / Azure AD (Phase 1)
 *
 * @module firebase-auth-providers
 */

import { OAuthProvider, GoogleAuthProvider } from "firebase/auth";

/**
 * Google OAuth 2.0 Provider
 *
 * Pre-configured GoogleAuthProvider for Firebase Authentication.
 * Scopes are automatically set by Firebase SDK (profile, email).
 *
 * @example
 * ```typescript
 * import { googleProvider } from './firebase-auth-providers';
 * import { signInWithPopup, auth } from 'firebase/auth';
 *
 * const result = await signInWithPopup(auth, googleProvider);
 * ```
 */
export const googleProvider = new GoogleAuthProvider();

/**
 * Apple Sign-In Provider
 *
 * Pre-configured OAuthProvider for Apple Sign-In.
 * Requests email and name scopes.
 * Supports Apple's privacy relay email addresses.
 *
 * Scopes:
 * - email: User's email (may be privatized via relay)
 * - name: User's first/last name (only provided on first sign-in)
 *
 * @example
 * ```typescript
 * import { appleProvider } from './firebase-auth-providers';
 * import { signInWithPopup, auth } from 'firebase/auth';
 *
 * const result = await signInWithPopup(auth, appleProvider);
 * ```
 */
export const appleProvider = new OAuthProvider("apple.com");
appleProvider.addScope("email");
appleProvider.addScope("name");
appleProvider.setCustomParameters({
  locale: "en", // Language preference for Apple's consent screen
});

/**
 * Microsoft OAuth 2.0 Provider (Azure AD / Entra ID)
 *
 * Pre-configured OAuthProvider for Microsoft accounts.
 * Supports personal Microsoft accounts and Azure AD organizational accounts.
 *
 * Scopes:
 * - openid: OpenID Connect identity token
 * - profile: User's profile information
 * - email: User's email address
 *
 * Custom Parameters:
 * - tenant: "common" allows any Microsoft account type
 * - prompt: "select_account" always shows account selection screen
 *
 * @example
 * ```typescript
 * import { microsoftProvider } from './firebase-auth-providers';
 * import { signInWithPopup, auth } from 'firebase/auth';
 *
 * const result = await signInWithPopup(auth, microsoftProvider);
 * ```
 */
export const microsoftProvider = new OAuthProvider("microsoft.com");
microsoftProvider.addScope("openid");
microsoftProvider.addScope("profile");
microsoftProvider.addScope("email");
microsoftProvider.setCustomParameters({
  tenant: "common", // Allow any Microsoft account (personal or organizational)
  prompt: "select_account", // Always show account selection screen
});
